import React, { FC, useEffect, useState } from 'react';
import {
	Alert,
	Checkbox,
	FormControlLabel,
	TextField,
} from '@mui/material';
import styled from 'styled-components';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { useSnackbar } from '@Providers/useSnackbar';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { FlexLayout, GridLayout } from '@Styled/utilities';
import {
	videoProviderErrorMessage,
	videoProvidersApplyBunnyTemplate,
	videoProvidersUpdate,
} from '../Slices/VideoProviderSettings.slice';
import {
	ApplyTemplateResult,
	BunnyLibraryTemplate,
	DEFAULT_BUNNY_LIBRARY_TEMPLATE,
	VideoProviderSetting,
} from '../Types/VideoProviders.types';
import {
	buildCredentialsPatch,
	CardNote,
	CardSectionTitle,
	emptySecretDraft,
	ProviderCard,
	SecretDraft,
	SecretField,
	storedSecret,
} from './ProviderCardShared';

type TemplateBooleanKey =
	| 'EnableDRM'
	| 'EnableMP4Fallback'
	| 'KeepOriginalFiles'
	| 'AllowDirectPlay'
	| 'BlockNoneReferrer'
	| 'RememberPlayerPosition';

const TEMPLATE_BOOLEANS: Array<{ key: TemplateBooleanKey; label: string }> = [
	{ key: 'EnableDRM', label: 'DRM (MediaCage)' },
	{ key: 'EnableMP4Fallback', label: 'MP4 fallback' },
	{ key: 'KeepOriginalFiles', label: 'Keep original files' },
	{ key: 'AllowDirectPlay', label: 'Allow direct play' },
	{ key: 'BlockNoneReferrer', label: 'Block empty referrer' },
	{ key: 'RememberPlayerPosition', label: 'Remember player position' },
];

type Props = {
	setting: VideoProviderSetting;
};

const BunnyCard: FC<Props> = ({ setting }) => {
	const dispatch = useAppThunkDispatch();
	const { displaySnackbar } = useSnackbar();
	const { loaders } = useTypedSelector((state) => state.VideoProviderSettings);

	const [enabled, setEnabled] = useState(setting.enabled);
	const [accountKeyDraft, setAccountKeyDraft] = useState<SecretDraft>(
		emptySecretDraft()
	);
	const [fixedLibraryId, setFixedLibraryId] = useState('');
	const [fixedLibraryKeyDraft, setFixedLibraryKeyDraft] =
		useState<SecretDraft>(emptySecretDraft());
	const [videoApiHost, setVideoApiHost] = useState('');
	const [template, setTemplate] = useState<BunnyLibraryTemplate>(
		DEFAULT_BUNNY_LIBRARY_TEMPLATE
	);
	const [allowedReferrersText, setAllowedReferrersText] = useState('');
	const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
	const [applyResult, setApplyResult] = useState<ApplyTemplateResult | null>(
		null
	);

	// Re-seed from the masked row on every fetch (mount + after saves).
	useEffect(() => {
		setEnabled(setting.enabled);
		setAccountKeyDraft(emptySecretDraft());
		setFixedLibraryKeyDraft(emptySecretDraft());
		const storedFixedId = setting.credentials?.fixed_library_id;
		setFixedLibraryId(typeof storedFixedId === 'string' ? storedFixedId : '');
		setVideoApiHost(setting.config?.video_api_host ?? '');
		const storedTemplate = setting.config?.library_template as
			| BunnyLibraryTemplate
			| undefined;
		const seeded = {
			...DEFAULT_BUNNY_LIBRARY_TEMPLATE,
			...(storedTemplate ?? {}),
		};
		setTemplate(seeded);
		setAllowedReferrersText((seeded.AllowedReferrers ?? []).join(', '));
	}, [setting]);

	const setTemplateField = <K extends keyof BunnyLibraryTemplate>(
		key: K,
		value: BunnyLibraryTemplate[K]
	) => setTemplate((current) => ({ ...current, [key]: value }));

	const save = async () => {
		try {
			await dispatch(
				videoProvidersUpdate({
					provider: 'bunny',
					enabled,
					credentials: buildCredentialsPatch(
						{
							account_api_key: accountKeyDraft,
							fixed_library_api_key: fixedLibraryKeyDraft,
						},
						{ fixed_library_id: fixedLibraryId.trim() || null }
					),
					config: {
						video_api_host: videoApiHost.trim() || null,
						library_template: {
							...template,
							AllowedReferrers: allowedReferrersText
								.split(',')
								.map((referrer) => referrer.trim())
								.filter(Boolean),
						},
					},
				})
			).unwrap();
			displaySnackbar('success', 'Bunny Stream settings saved');
		} catch (error: any) {
			displaySnackbar(
				'error',
				videoProviderErrorMessage(error, 'Unable to save Bunny Stream settings')
			);
		}
	};

	const onApplyTemplateConfirm = async (close: boolean, confirm?: boolean) => {
		setApplyConfirmOpen(false);
		if (!confirm) return;
		try {
			const { data } = await dispatch(
				videoProvidersApplyBunnyTemplate()
			).unwrap();
			setApplyResult(data);
			displaySnackbar(
				data.failed.length ? 'error' : 'success',
				`Template applied to ${data.applied} librar${
					data.applied === 1 ? 'y' : 'ies'
				}${data.failed.length ? `, ${data.failed.length} failed` : ''}`
			);
		} catch (error: any) {
			setApplyResult(null);
			displaySnackbar(
				'error',
				videoProviderErrorMessage(error, 'Unable to apply the library template')
			);
		}
	};

	return (
		<ProviderCard
			title="Bunny Stream"
			subtitle="One library per instructor; collections per course"
			enabled={enabled}
			onEnabledChange={setEnabled}
			onSave={save}
			saving={loaders.update === 'pending'}
		>
			<SecretField
				label="Account API key"
				stored={storedSecret(setting.credentials, 'account_api_key')}
				draft={accountKeyDraft}
				onChange={setAccountKeyDraft}
			/>

			<CardSectionTitle>Library settings template</CardSectionTitle>
			<CardNote>
				Applied to every auto-created instructor library. Token authentication
				stays off — the student player has no signing.
			</CardNote>
			<TemplateGrid>
				<TextField
					size="small"
					label="Enabled resolutions"
					value={template.EnabledResolutions ?? ''}
					onChange={(event) =>
						setTemplateField('EnabledResolutions', event.target.value)
					}
				/>
				<TextField
					size="small"
					label="Player key color"
					value={template.PlayerKeyColor ?? ''}
					onChange={(event) =>
						setTemplateField('PlayerKeyColor', event.target.value)
					}
				/>
				<TextField
					size="small"
					label="Font family"
					value={template.FontFamily ?? ''}
					onChange={(event) =>
						setTemplateField('FontFamily', event.target.value)
					}
				/>
				<TextField
					size="small"
					label="Playback speeds"
					value={template.PlaybackSpeeds ?? ''}
					onChange={(event) =>
						setTemplateField('PlaybackSpeeds', event.target.value)
					}
				/>
			</TemplateGrid>
			<TextField
				size="small"
				fullWidth
				label="Player controls"
				value={template.Controls ?? ''}
				onChange={(event) => setTemplateField('Controls', event.target.value)}
			/>
			<TemplateGrid>
				{TEMPLATE_BOOLEANS.map(({ key, label }) => (
					<FormControlLabel
						key={key}
						label={label}
						control={
							<Checkbox
								checked={!!template[key]}
								onChange={(event) =>
									setTemplateField(key, event.target.checked)
								}
							/>
						}
					/>
				))}
			</TemplateGrid>
			<div>
				<TextField
					size="small"
					fullWidth
					label="Allowed referrers"
					placeholder="*.eduact.me, eduact.me"
					value={allowedReferrersText}
					onChange={(event) => setAllowedReferrersText(event.target.value)}
				/>
				{/* Plain-flow note instead of TextField helperText: the theme
				    positions helper text absolutely, so it overlaps whatever
				    element follows the field. */}
				<CardNote>
					Comma-separated. Per-environment — abwaab domains need their own
					list.
				</CardNote>
			</div>
			<FlexLayout alignItems="center" gridGap="1rem">
				<EdButton
					variant="outlined"
					disabled={loaders.applyTemplate === 'pending'}
					onClick={() => setApplyConfirmOpen(true)}
				>
					Apply to all existing libraries
				</EdButton>
				<CardNote>
					Re-applies the saved template so older libraries do not drift.
				</CardNote>
			</FlexLayout>
			{applyResult && applyResult.failed.length > 0 && (
				<Alert severity="warning" onClose={() => setApplyResult(null)}>
					Applied to {applyResult.applied}; failed for{' '}
					{applyResult.failed
						.map((failure) => `library ${failure.library_id} (${failure.error})`)
						.join(', ')}
				</Alert>
			)}

			<CardSectionTitle>Advanced</CardSectionTitle>
			<TemplateGrid>
				<TextField
					size="small"
					label="Video API host"
					placeholder="https://video.bunnycdn.com"
					value={videoApiHost}
					onChange={(event) => setVideoApiHost(event.target.value)}
				/>
				<TextField
					size="small"
					label="Fixed library ID (override)"
					value={fixedLibraryId}
					onChange={(event) => setFixedLibraryId(event.target.value)}
					helperText="Set to pin all imports to one library"
				/>
			</TemplateGrid>
			<SecretField
				label="Fixed library API key"
				stored={storedSecret(setting.credentials, 'fixed_library_api_key')}
				draft={fixedLibraryKeyDraft}
				onChange={setFixedLibraryKeyDraft}
			/>

			<EdConfirmModal
				open={applyConfirmOpen}
				handleClose={onApplyTemplateConfirm}
				text="Re-apply the saved library template to ALL existing Bunny libraries?"
			/>
		</ProviderCard>
	);
};

const TemplateGrid = styled(GridLayout)`
	grid-template-columns: 1fr 1fr;
	gap: 1rem;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

export default BunnyCard;
